import type { SqlRunner } from '../../../lib/db.js';
import type {
  AIProcessingJobQueueJobProcessor,
  CompanyAIProcessingJob,
} from '../../contracts/schemas/ai-processing-job.js';

/** The columns a file's AI processing jobs can be ordered by. */
export const AI_PROCESSING_JOBS_SORT_COLUMNS = ['created_at', 'job_type'];

export interface ListAiProcessingJobsParams {
  /** Only this file's jobs. */
  fileId?: string;
  /** Only the jobs this company created. */
  companyId?: string;
  /** Exact job type, e.g. `EXTRACTION`. */
  jobType?: string;
  /** 1-based. */
  page: number;
  limit: number;
  /** One of `AI_PROCESSING_JOBS_SORT_COLUMNS`; anything else sorts by `created_at`. */
  sort: string;
  /** `asc` ascends; anything else descends. */
  order: string;
}

type RawProcessor = Omit<AIProcessingJobQueueJobProcessor, 'createdAt'> & {
  createdAt: string | Date;
};

/** One file's jobs, whoever created them — the admin file page's list. */
export type ListFileAiProcessingJobsParams = ListAiProcessingJobsParams & {
  fileId: string;
};

type RawAiProcessingJobRow = Omit<
  CompanyAIProcessingJob,
  'queueJobProcessor'
> & {
  queueJobProcessor: string | RawProcessor | null;
  count: string | number;
};

const processorJson = (table: string): string => `jsonb_build_object(
  'id', ${table}.id,
  'jobId', ${table}.job_id,
  'processorNamespace', ${table}.processor_namespace,
  'status', ${table}.status,
  'priority', ${table}.priority,
  'scheduledAt', ${table}.scheduled_at,
  'processedAt', ${table}.processed_at,
  'processingStartedAt', ${table}.processing_started_at,
  'attempts', ${table}.attempts,
  'maxAttempts', ${table}.max_attempts,
  'error', ${table}.error,
  'createdAt', ${table}.created_at
)`;

/**
 * The live queue processor, else the archived one, else — for a job that
 * never went through the queue but has a result — a synthetic `skipped` one.
 */
const QUEUE_JOB_PROCESSOR = `
  CASE
    WHEN queue_job_processors.id IS NOT NULL THEN ${processorJson('queue_job_processors')}
    WHEN queue_job_processors_history.id IS NOT NULL THEN ${processorJson('queue_job_processors_history')}
    WHEN ai_processing_jobs.queue_job_processor_id IS NULL AND ai_processing_jobs.result IS NOT NULL THEN jsonb_build_object(
      'id', NULL,
      'jobId', NULL,
      'processorNamespace', NULL,
      'status', 'skipped',
      'priority', 0,
      'scheduledAt', NULL,
      'processedAt', NULL,
      'processingStartedAt', NULL,
      'attempts', 0,
      'maxAttempts', 0,
      'error', ai_processing_jobs.result->>'error',
      'createdAt', ai_processing_jobs.created_at
    )
    ELSE NULL
  END AS queue_job_processor`;

/** The processor arrives as JSON: parsed if the driver left it a string, its date revived. */
const toProcessor = (
  value: RawAiProcessingJobRow['queueJobProcessor'],
): AIProcessingJobQueueJobProcessor | null => {
  let processor: RawProcessor | null;
  if (typeof value === 'string') {
    try {
      processor = JSON.parse(value);
    } catch {
      processor = null;
    }
  } else {
    processor = value;
  }
  if (!processor) return null;
  return {
    ...processor,
    createdAt:
      typeof processor.createdAt === 'string'
        ? new Date(processor.createdAt)
        : processor.createdAt,
  };
};

/**
 * A page of AI processing jobs — a file's, a company's, or a company's on one
 * file — each with who created it and the queue processor that ran it.
 */
export const listAiProcessingJobsDb = async (
  db: SqlRunner,
  params: ListAiProcessingJobsParams &
    ({ fileId: string } | { companyId: string }),
): Promise<{ rows: CompanyAIProcessingJob[]; totalCount: number }> => {
  const values: unknown[] = [];
  const bind = (value: unknown): string => {
    values.push(value);
    return `$${values.length}`;
  };

  const conds: string[] = [];
  if (params.fileId) {
    conds.push(`ai_processing_jobs.file_id = ${bind(params.fileId)}`);
  }
  if (params.companyId) {
    conds.push(
      `ai_processing_jobs.created_by_company_id = ${bind(params.companyId)}`,
    );
  }
  if (params.jobType) {
    conds.push(`ai_processing_jobs.job_type = ${bind(params.jobType)}`);
  }
  const sort = AI_PROCESSING_JOBS_SORT_COLUMNS.includes(params.sort)
    ? params.sort
    : 'created_at';
  const order = params.order === 'asc' ? 'ASC' : 'DESC';
  const limitPh = bind(params.limit);
  const offsetPh = bind((params.page - 1) * params.limit);

  const rows = await db.runSql<RawAiProcessingJobRow>(
    `
      SELECT
        ai_processing_jobs.id,
        ai_processing_jobs.file_id,
        ai_processing_jobs.job_type,
        ai_processing_jobs.input,
        ai_processing_jobs.result,
        ai_processing_jobs.provider,
        ai_processing_jobs.model,
        ai_processing_jobs.input_tokens,
        ai_processing_jobs.output_tokens,
        ai_processing_jobs.total_tokens,
        ai_processing_jobs.completed_in_ms,
        ai_processing_jobs.created_at,
        ai_processing_jobs.created_by_company_id,
        ai_processing_jobs.created_by_user_id,
        ai_processing_jobs.created_by_api_key_id,
        ${QUEUE_JOB_PROCESSOR},
        COUNT(ai_processing_jobs.id) OVER () AS count
      FROM ai_processing_jobs
      LEFT JOIN queue_job_processors
        ON queue_job_processors.id = ai_processing_jobs.queue_job_processor_id
      LEFT JOIN queue_job_processors_history
        ON queue_job_processors_history.id = ai_processing_jobs.queue_job_processor_id
        AND queue_job_processors.id IS NULL
      WHERE ${conds.join(' AND ')}
      ORDER BY ai_processing_jobs.${sort} ${order}
      LIMIT ${limitPh} OFFSET ${offsetPh}
    `,
    values,
  );

  return {
    rows: rows.map(({ count: _count, queueJobProcessor, ...row }) => ({
      ...row,
      queueJobProcessor: toProcessor(queueJobProcessor),
    })),
    totalCount: rows.length > 0 ? Number(rows[0].count) : 0,
  };
};
